import React from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import SortingAccordion from '../SortingAccordion';
import FourWayDotIcon from 'assets/img/icons/FourWayDotIcon';
import StarShineIcon from 'assets/img/icons/StarShineIcon';
import Sum from 'components/Sum';
import { BORDER_COLOR, BRIGHT_BLUE, CLOUD_MIST, DARKER_GREY, LAVENDER_STEEL } from 'utils/theme';
import NoSourcesTooltip from '../NoSourcesTooltip/NoSourcesTooltip';
import { getClaimKeys } from 'pages/PlaygroundV2Page/Claim/helper';
import SwapIcon from 'assets/img/icons/SwapIcon';

interface ClaimActionsProps {
  claim: Claim;
  expandedClaim: string | null;
  setExpanded: (key: string | null) => void;
}

const ClaimActions = ({ claim, expandedClaim, setExpanded }: ClaimActionsProps) => {
  const { summaryKey, rephraseKey } = getClaimKeys(claim.claim);

  const isSummaryOpen = expandedClaim === summaryKey;
  const isRephraseOpen = expandedClaim === rephraseKey;
  const isSourceOpen = expandedClaim === claim.claim;

  const showActions = claim?.evidence?.length || claim.isBeingChecked;
  const showNoSources = !claim?.evidence?.length && !claim.isBeingChecked && claim.hasBeenChecked;

  const SummaryButton = (
    <Button
      onClick={() => setExpanded(isSummaryOpen ? null : summaryKey)}
      sx={{
        borderBottom: isSummaryOpen ? `2.5px solid ${LAVENDER_STEEL}` : 'none',
        borderRadius: '4px 4px 0px 0px',
        textTransform: 'none',
        padding: '16px',
      }}
      startIcon={<StarShineIcon />}
      disabled={claim.isBeingChecked}
    >
      <Typography fontSize="16px" color={DARKER_GREY}>
        Summary
      </Typography>
    </Button>
  );

  const SourcesButton = (
    <Button
      onClick={() => setExpanded(isSourceOpen ? null : claim.claim)}
      sx={{
        borderBottom: isSourceOpen ? `2.5px solid ${LAVENDER_STEEL}` : 'none',
        borderRadius: '4px 4px 0px 0px',
        textTransform: 'none',
        padding: '16px',
      }}
      startIcon={<FourWayDotIcon />}
      disabled={claim.isBeingChecked}
    >
      <Stack direction="row" alignItems="center">
        <Typography fontSize="16px" mr={1} color={DARKER_GREY}>
          Sources
        </Typography>
        <Sum
          color={CLOUD_MIST}
          count={claim.evidence?.length}
          isSum
          isLoading={claim.isBeingChecked}
        />
      </Stack>
    </Button>
  );

  const NoSourcesLabel = (
    <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
      <Sum
        color={CLOUD_MIST}
        count={claim.evidence?.length}
        isSum
        isLoading={claim.isBeingChecked}
      />
      <Typography fontSize="16px" color={DARKER_GREY} fontWeight="500" lineHeight="20px">
        No sources found
      </Typography>
      <Box sx={{ mt: '4px' }}>
        <NoSourcesTooltip />
      </Box>
    </Box>
  );

  const RephraseButton = (
    <Button
      onClick={() => setExpanded(isRephraseOpen ? null : rephraseKey)}
      sx={{
        textTransform: 'none',
        color: DARKER_GREY,
        borderBottom: isRephraseOpen ? `2.5px solid ${BRIGHT_BLUE}` : 'none',
        borderRadius: '4px 4px 0px 0px',
        padding: '16px',
        gap: '8px',
        fontSize: '16px',
        fontWeight: '500',
      }}
    >
      <SwapIcon />
      Rephrase
    </Button>
  );

  return (
    <>
      {showActions && (
        <Box sx={{ mt: 1 }}>
          <Stack direction="row" gap={2}>
            {SummaryButton}
            {SourcesButton}
          </Stack>
        </Box>
      )}

      {isSourceOpen && claim?.evidence && (
        <Box
          sx={{
            borderTop: `1px solid ${BORDER_COLOR}`,
          }}
        >
          <SortingAccordion claimId={claim.claim} evidence={claim.evidence} />
        </Box>
      )}

      {showNoSources && (
        <Box mt={2}>
          <Stack direction="row" spacing={2} alignItems="center">
            {NoSourcesLabel}
            {RephraseButton}
          </Stack>
        </Box>
      )}
    </>
  );
};

export default ClaimActions;
